// src/components/MultiDropdown.js
import React, { useEffect, useMemo, useRef, useState } from 'react';

export default function MultiDropdown({ values=[], onChange, options, placeholder='All', className='', hideSearch=false }) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const rootRef = useRef(null);
  const searchRef = useRef(null);

  const allItems = useMemo(()=> options.map(o =>
    typeof o === 'object' ? o : { value: o, label: String(o ?? '—') }
  ), [options]);

  const filteredItems = useMemo(() =>
    allItems.filter(item =>
      item.label.toLowerCase().includes(searchTerm.toLowerCase())
    ), [allItems, searchTerm]);

  useEffect(()=>{
    const onDoc = (e)=>{ if (!rootRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return ()=> document.removeEventListener('mousedown', onDoc);
  },[]);

  useEffect(() => {
    if (open) {
      setSearchTerm('');
      if (!hideSearch) {
        setTimeout(() => searchRef.current?.focus(), 100);
      }
    }
  }, [open, hideSearch]);

  const setVal = (val, checked)=>{
    const set = new Set(values);
    if (checked) set.add(val); else set.delete(val);
    onChange(Array.from(set));
  };

  const handleSelectAll = (e) => {
    const currentValues = new Set(values);
    const visibleValues = new Set(filteredItems.map(i => i.value));
    
    if (e.target.checked) {
      visibleValues.forEach(val => currentValues.add(val));
    } else {
      visibleValues.forEach(val => currentValues.delete(val));
    }
    onChange(Array.from(currentValues));
  };

  const label = values.length ? values.slice(0,2).join(', ') + (values.length>2 ? ` +${values.length-2}` : '') : placeholder;

  const areAllVisibleSelected = useMemo(() => {
    if (filteredItems.length === 0) return false;
    return filteredItems.every(item => values.includes(item.value));
  }, [filteredItems, values]);

  return (
    <div ref={rootRef} className={`dd ${open ? 'open' : ''} ${className}`}>
      <button className="dd-head" type="button" onClick={()=>setOpen(o=>!o)}>
        <span className={`dd-value ${values.length ? '' : 'muted'}`}>{label}</span>
        <span className="dd-caret" />
      </button>
      {open && (
        <div className="dd-menu">
          {!hideSearch &&
            <div className="dd-search">
              <input
                ref={searchRef}
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                onClick={e => e.stopPropagation()}
              />
            </div>
          }
          {!hideSearch &&
            <label className="dd-item chk">
              <input type="checkbox" checked={areAllVisibleSelected} onChange={handleSelectAll} />
              <span>(Select all visible)</span>
            </label>
          }
          <div className="dd-item" onClick={()=>{ onChange([]); }}>{placeholder}</div>
          {filteredItems.map((i,ix)=>{
            const checked = values.includes(i.value);
            return (
              <label key={ix} className={`dd-item chk ${checked ? 'selected' : ''} ${i.disabled ? 'disabled' : ''}`}>
                <input type="checkbox" checked={checked} onChange={e=>setVal(i.value, e.target.checked)} disabled={i.disabled} />
                <span>{i.label}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}